import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Upload, Trash2, Loader2, FileText, Film, ImageIcon, Search, Pencil, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { adminApi, mediaRaw } from "./adminApi";
import { Field } from "./components";

const KINDS = [{ k: "", label: "All" }, { k: "image", label: "Images" }, { k: "video", label: "Videos" }, { k: "pdf", label: "PDFs" }];
const ICON = { image: ImageIcon, video: Film, pdf: FileText };

export default function MediaAdmin() {
  const qc = useQueryClient();
  const [kind, setKind] = useState("");
  const [q, setQ] = useState("");
  const [uploading, setUploading] = useState(false);
  const [pct, setPct] = useState(0);
  const [editing, setEditing] = useState(null);

  const { data, isLoading } = useQuery({ queryKey: ["admin-media", kind, q], queryFn: () => adminApi.listMedia(kind || undefined, q || undefined) });
  const refresh = () => qc.invalidateQueries({ queryKey: ["admin-media"] });
  const list = data || [];

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setPct(0);
    try { await adminApi.uploadMedia(file, file.name, "", setPct); toast.success("Uploaded"); refresh(); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const remove = async (m) => {
    if (!window.confirm(`Delete "${m.title}"?`)) return;
    try { await adminApi.deleteMedia(m.id, false); toast.success("Deleted"); refresh(); }
    catch (err) {
      if (err.response?.status === 409) {
        if (window.confirm(err.response.data.detail + " Delete anyway?")) { await adminApi.deleteMedia(m.id, true); toast.success("Deleted"); refresh(); }
      } else toast.error("Delete failed");
    }
  };

  return (
    <div data-testid="admin-media-library">
      <h1 className="font-head text-3xl font-extrabold text-white">Media Library</h1>
      <p className="mt-1 text-slate-400">Upload and manage images, videos and PDF documents.</p>

      <div className="mt-6 flex flex-wrap items-center gap-3">
        <label className="flex cursor-pointer items-center gap-2 rounded-xl cyan-grad px-5 py-2.5 font-bold text-[#041021]" data-testid="media-upload-btn">
          {uploading ? <><Loader2 className="h-4 w-4 animate-spin" /> {pct}%</> : <><Upload className="h-4 w-4" /> Upload</>}
          <input type="file" className="hidden" onChange={upload} disabled={uploading} />
        </label>
        <div className="flex items-center gap-2 rounded-xl border border-slate-700 bg-[#0b1730] px-3">
          <Search className="h-4 w-4 text-slate-400" />
          <input data-testid="media-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="bg-transparent py-2.5 text-sm text-white outline-none" />
        </div>
        <div className="flex gap-1">
          {KINDS.map((x) => (
            <button key={x.k} onClick={() => setKind(x.k)} data-testid={`media-filter-${x.label.toLowerCase()}`}
              className={`rounded-lg px-3 py-2 text-sm ${kind === x.k ? "cyan-grad text-[#041021] font-semibold" : "bg-[#0b1730] text-slate-300"}`}>{x.label}</button>
          ))}
        </div>
      </div>

      {isLoading ? <Loader2 className="mt-10 h-8 w-8 animate-spin text-cyan-400" /> : (
        <div className="mt-6 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
          {list.length === 0 && <p className="col-span-full rounded-2xl glass p-8 text-center text-slate-500">No media found.</p>}
          {list.map((m) => {
            const I = ICON[m.kind] || ImageIcon;
            return (
              <div key={m.id} data-testid={`media-card-${m.id}`} className="overflow-hidden rounded-2xl glass">
                {m.kind === "image" ? <img src={mediaRaw(m.id)} alt={m.title} className="h-32 w-full object-cover" />
                  : <div className="flex h-32 items-center justify-center bg-[#122244]"><I className="h-10 w-10 text-cyan-300" /></div>}
                <div className="p-3">
                  <p className="truncate text-sm font-medium text-white">{m.title}</p>
                  <p className="truncate text-xs text-slate-500">{(m.size / 1024).toFixed(0)} KB · {m.kind}</p>
                  <div className="mt-2 flex gap-1">
                    <button onClick={() => setEditing(m)} className="rounded-lg p-1.5 text-slate-400 hover:text-cyan-300"><Pencil className="h-4 w-4" /></button>
                    <button onClick={() => remove(m)} data-testid={`media-delete-${m.id}`} className="rounded-lg p-1.5 text-slate-400 hover:text-rose-300"><Trash2 className="h-4 w-4" /></button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && <MetaEditor media={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); refresh(); }} />}
    </div>
  );
}

function MetaEditor({ media, onClose, onSaved }) {
  const [title, setTitle] = useState(media.title);
  const [description, setDescription] = useState(media.description || "");
  const [saving, setSaving] = useState(false);
  const save = async () => { setSaving(true); try { await adminApi.updateMediaMeta(media.id, title, description); toast.success("Saved"); onSaved(); } finally { setSaving(false); } };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-[#0b1730] border-cyan-500/20 text-white">
        <DialogHeader><DialogTitle className="font-head">Edit Media</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <Field label="Title"><Input value={title} onChange={(e) => setTitle(e.target.value)} className="bg-[#050b18] border-slate-700" /></Field>
          <Field label="Description"><Input value={description} onChange={(e) => setDescription(e.target.value)} className="bg-[#050b18] border-slate-700" /></Field>
        </div>
        <DialogFooter><Button variant="ghost" onClick={onClose}>Cancel</Button><Button className="cyan-grad text-[#041021] font-bold" disabled={saving} onClick={save}>Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
